import { useState, useEffect, act } from "react";
import PropTypes from "prop-types";
import { Table as BTable, Modal, Button, Spinner } from "react-bootstrap";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useToken } from "../../hooks/tokenContext";
import useApi from "../../hooks/apiService";
import { useTranslation } from "react-i18next";
import IconToSolve from "../../components/icon";

function Logs({ isAdminView = false }) {
  const { t } = useTranslation();

  const { fetchData } = useApi();
  const { token } = useToken();
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const IGNORED_KEYS = ["last_sample"];
  // Estados para paginação e filtros do backend
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [orderBy, setOrderBy] = useState("occurred_at");
  const [direction, setDirection] = useState("desc");
  const [loading, setLoading] = useState(false);

  const baseEndpoint = isAdminView ? "/super-admin/audit-logs" : "/audit-logs";

  useEffect(() => {
    const fetchDataLogs = async () => {
      setLoading(true);
      try {
        // Construir parâmetros da query
        const params = new URLSearchParams({
          page: pageIndex + 1,
          items_per_page: pageSize,
        });

        if (orderBy && direction) {
          params.append("order_by", orderBy);
          params.append("direction", direction);
        }
        const response = await fetchData(
          `${baseEndpoint}?${params.toString()}`,
        );

        const formattedData = [
          ...(response.data || []).map((item) => ({
            ...item,
            occurred_at: new Date(item.occurred_at).toLocaleString("pt-BR"),
          })),
        ];

        setData(formattedData);
        setTotalCount(response.total_count || 0);
        setHasMore(response.has_more || false);
      } catch (error) {
        console.error("Erro ao buscar os dados:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDataLogs();
  }, [token, pageIndex, pageSize, orderBy, direction, baseEndpoint]);

  const openModal = (rowData) => {
    setSelectedRow(rowData);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };
  function formatFieldName(field) {
    if (!field) return "";

    return field
      .replace(/_/g, " ") // user_name → user name
      .replace(/\b\w/g, (char) => char.toUpperCase()); // primeira letra maiúscula
  }

  const [exportingCSV, setExportingCSV] = useState(false);

  // Função para exportar CSV - busca todos os dados
  const handleExportCSV = async () => {
    setExportingCSV(true);
    try {
      const params = new URLSearchParams({
        page: 1,
        items_per_page: totalCount || 10000, // Buscar todos
        order_by: orderBy,
        direction: direction,
      });

      const response = await fetchData(`${baseEndpoint}?${params.toString()}`);

      const csvData = (response.data || []).map((row) => ({
        Usuário: row.user_name,
        "Data e hora": new Date(row.occurred_at).toLocaleString("pt-BR"),
        Ação: getAction(row.action),
        [t("log_category")]: getContextLabel(row.context),
      }));

      // Converter para CSV e fazer download
      const headers = Object.keys(csvData[0] || {});
      const csvContent = [
        headers.join(","),
        ...csvData.map((row) =>
          headers.map((header) => `"${row[header] || ""}"`).join(","),
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "logs.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      alert("Erro ao exportar CSV. Tente novamente.");
    } finally {
      setExportingCSV(false);
    }
  };

  const handleSort = (columnId) => {
    if (orderBy !== columnId) {
      // First click → DESC
      setOrderBy(columnId);
      setDirection("desc");
    } else if (direction === "desc") {
      // Second click → ASC
      setDirection("asc");
    } else {
      // Third click → REMOVE SORT
      setOrderBy(null);
      setDirection(null);
    }

    setPageIndex(0);
  };

  const columns = [
    {
      header: t("Usuário"),
      accessorKey: "user_name",
      cell: (info) => info.getValue(),
    },
    {
      header: t("Data e hora"),
      accessorKey: "occurred_at",
      cell: (info) => info.getValue() || "-",
    },
    {
      header: t("Ação"),
      accessorKey: "action",
      cell: (info) => getAction(info.getValue()),
    },
    {
      header: t("log_category"),
      accessorKey: "context",
      cell: (info) => getContextLabel(info.getValue()),
    },
    {
      id: "acoes",
      header: (
        <button
          className="btn-table"
          onClick={handleExportCSV}
          disabled={exportingCSV || loading}
          title={t("Exportar para CSV")}
        >
          {exportingCSV ? (
            <Spinner size="sm" as="span" animation="border" />
          ) : (
            <>
              <IconToSolve
                className={"pt-2"}
                iconName={"Csv"}
                width={35}
                height={35}
              />{" "}
            </>
          )}
        </button>
      ),
      cell: (info) => (
        <button
          className="btn-table btn-editar bi bi-three-dots"
          onClick={() => openModal(info.row.original)}
          title={t("Mais detalhes")}
        ></button>
      ),
      enableSorting: false,
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true, // Importante: paginação manual
    manualSorting: true, // Importante: ordenação manual
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
  });

  function getAction(action) {
    switch (action) {
      case "C":
        return t("Criação");
      case "U":
        return t("Atualização");
      case "L":
        return t("Login");
      case "D":
        return t("Remoção");
      case "X":
        return t("Envio de comando");
      default:
        return "Unknown";
    }
  }

  function getContextLabel(context) {
    if (!context) return "-";
    const key = `context_${context}`;
    const translated = t(key);
    return translated !== key ? translated : formatFieldName(context);
  }

  function compareAndHighlight(asFound, asLeft) {
    const asFoundObj = asFound ? parseDeep(asFound) : {};
    const asLeftObj = asLeft ? parseDeep(asLeft) : {};

    function deepCompare(foundValue, leftValue, path = []) {
      const differences = [];

      if (
        foundValue &&
        typeof foundValue === "object" &&
        leftValue &&
        typeof leftValue === "object"
      ) {
        const allKeys = new Set([
          ...Object.keys(foundValue || {}),
          ...Object.keys(leftValue || {}),
        ]);

        for (const key of allKeys) {
          const newPath = [...path, key];
          differences.push(
            ...deepCompare(foundValue[key], leftValue[key], newPath),
          );
        }
      } else if (Array.isArray(foundValue) && Array.isArray(leftValue)) {
        if (
          foundValue.length !== leftValue.length ||
          !foundValue.every((item, index) => item === leftValue[index])
        ) {
          differences.push({ path, foundValue, leftValue, isChanged: true });
        }
      } else if (foundValue !== leftValue) {
        differences.push({ path, foundValue, leftValue, isChanged: true });
      } else {
        differences.push({ path, foundValue, leftValue, isChanged: false });
      }
      return differences;
    }

    return deepCompare(asFoundObj, asLeftObj);
  }

  function formatPath(path) {
    return path.map(formatFieldName).join(" > ");
  }

  function parseDeep(value) {
    if (value == null || value === "undefined") return "-";

    if (Array.isArray(value)) {
      return value.map(parseDeep);
    }

    if (typeof value === "object") {
      const result = {};

      for (const [key, val] of Object.entries(value)) {
        if (key.toLowerCase().includes("id") || IGNORED_KEYS.includes(key)) {
          continue;
        }

        result[key] = parseDeep(val);
      }

      return result;
    }

    return value;
  }

  // Renderiza objetos/arrays recursivamente
  function renderObjectRecursively(obj) {
    if (obj == null) return <span>{t("null_value")}</span>;

    if (Array.isArray(obj)) {
      if (obj.length === 0) return <span>{t("empty_array")}</span>;
      return (
        <ul>
          {obj.map((item, idx) => (
            <li key={idx}>{renderObjectRecursively(item)}</li>
          ))}
        </ul>
      );
    }

    if (typeof obj === "object") {
      const entries = Object.entries(obj);
      if (entries.length === 0) return <span>{"{}"}</span>;
      return (
        <ul>
          {entries.map(([key, value]) => (
            <li key={key}>
              <strong>
                {key}
                {t("colon")}
              </strong>{" "}
              {typeof value === "object" ? (
                renderObjectRecursively(value)
              ) : (
                <span>{String(value)}</span>
              )}
            </li>
          ))}
        </ul>
      );
    }

    return <span>{String(obj)}</span>;
  }

  const getSortIcon = (columnId) => {
    if (orderBy !== columnId) {
      return <i className="icn-header bi bi-arrow-down-up" />;
    }

    if (direction === "asc") {
      return <i className="icn-header bi bi-arrow-up" />;
    }

    if (direction === "desc") {
      return <i className="icn-header bi bi-arrow-down" />;
    }

    return <i className="icn-header bi bi-arrow-down-up" />;
  };

  return (
    <div>
      {!isAdminView && (
        <div className="div-profile mt-2">
          <IconToSolve iconName={"Info"} width={35} height={35} />{" "}
          <h3 className="m-0 alarm-title"> {t("logs")}</h3>
        </div>
      )}
      <div className="p-2 div-table">
        {loading && (
          <div
            className="position-absolute top-1 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ background: "rgba(146, 146, 146, 0.52)", zIndex: 10 }}
          >
            <Spinner animation="border" role="status" />
          </div>
        )}

        <BTable hover responsive size="sm" className="tabelas">
          <thead className="tabel-cabecalho">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    onClick={() => {
                      if (
                        header.column.columnDef.enableSorting !== false &&
                        index !== headerGroup.headers.length - 1
                      ) {
                        handleSort(header.column.id);
                      }
                    }}
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      cursor:
                        header.column.columnDef.enableSorting !== false &&
                        index !== headerGroup.headers.length - 1
                          ? "pointer"
                          : "default",
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    {index !== headerGroup.headers.length - 1 &&
                      getSortIcon(header.column.id)}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {data.length === 0 && !loading ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  {t("no_records_found")}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </BTable>

        <div className="pagination mt-3 align-items-center ">
          <Button
            onClick={() => setPageIndex((old) => Math.max(0, old - 1))}
            disabled={pageIndex === 0 || loading}
            title="página anterior"
            className="pagination-btn"
          >
            <i className="bi bi-chevron-left pagination-icon"></i>
          </Button>
          <span className="mx-3">
            {t("page")} {pageIndex + 1} {t("of")}{" "}
            {Math.ceil(totalCount / pageSize)}
          </span>

          <Button
            onClick={() => setPageIndex((old) => old + 1)}
            disabled={!hasMore || loading}
            title="próxima página"
            className="pagination-btn"
          >
            <i className="bi bi-chevron-right pagination-icon"></i>
          </Button>

          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPageIndex(0); // Reset para primeira página
            }}
            className="pagination-select"
            aria-label="Select de paginação"
            id="select"
            disabled={loading}
            style={{ height: "36px" }}
          >
            {[5, 10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <Modal
          scrollable
          backdrop="static"
          centered
          size="xl"
          show={showModal}
          onHide={closeModal}
        >
          <Modal.Header closeVariant="white" closeButton>
            <Modal.Title>{t("details")}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-break">
            {selectedRow && (
              <>
                <h3>{t("more_details")}</h3>
                <ul>
                  <li>
                    <strong>{t("user")}</strong> {selectedRow.user_name}
                  </li>
                  <li>
                    <strong>{t("date_time")}</strong> {selectedRow.occurred_at}
                  </li>
                  <li>
                    <strong>{t("action")}</strong>{" "}
                    {getAction(selectedRow.action)}
                  </li>
                  <li>
                    <strong>{t("log_category")}{t("colon")}</strong>{" "}
                    {getContextLabel(selectedRow.context)}
                  </li>
                  {selectedRow.context === "command" ||
                  selectedRow.action === "X" ? (
                    <li>
                      <strong>{t("Envio de comando")}</strong>
                      <ul>
                        {(() => {
                          let cmd = selectedRow.as_left;
                          if (typeof cmd === "string") {
                            try {
                              cmd = JSON.parse(cmd);
                            } catch {
                              cmd = {};
                            }
                          }
                          cmd = cmd || {};
                          const variable = cmd.variable || cmd.variable_id;
                          const asset = cmd.asset || cmd.asset_id;
                          const value = cmd.command;
                          return (
                            <>
                              <li>
                                <strong>{t("Variável")}:</strong>{" "}
                                {typeof variable === "object"
                                  ? renderObjectRecursively(variable)
                                  : String(variable ?? "-")}
                              </li>
                              <li>
                                <strong>{t("Ativo")}:</strong>{" "}
                                {typeof asset === "object"
                                  ? renderObjectRecursively(asset)
                                  : String(asset ?? "-")}
                              </li>
                              <li>
                                <strong>{t("Valor enviado")}:</strong>{" "}
                                {String(value ?? "-")}
                              </li>
                            </>
                          );
                        })()}
                      </ul>
                    </li>
                  ) : (
                    <li>
                      <strong>{getAction(selectedRow.action)}</strong>
                      <ul>
                        {selectedRow.as_found && !selectedRow.as_left ? (
                          renderObjectRecursively(
                            parseDeep(selectedRow.as_found),
                          )
                        ) : selectedRow.as_left && !selectedRow.as_found ? (
                          renderObjectRecursively(
                            parseDeep(selectedRow.as_left),
                          )
                        ) : selectedRow.as_found && selectedRow.as_left ? (
                          <ul>
                            {compareAndHighlight(
                              selectedRow.as_found,
                              selectedRow.as_left,
                            ).map(
                              ({ path, foundValue, leftValue, isChanged }) => (
                                <li
                                  key={path.join(".")}
                                  style={{
                                    color: isChanged ? "green" : "black",
                                  }}
                                >
                                  <strong>
                                    {formatPath(path)}
                                    {t("colon")}
                                  </strong>{" "}
                                  {isChanged ? (
                                    <>
                                      <span
                                        style={{
                                          textDecoration: "line-through",
                                          color: "red",
                                        }}
                                      >
                                        {typeof foundValue === "object"
                                          ? renderObjectRecursively(foundValue)
                                          : String(foundValue ?? null)}
                                      </span>{" "}
                                      {t("arrow_right")}{" "}
                                      <span>
                                        {typeof leftValue === "object"
                                          ? renderObjectRecursively(leftValue)
                                          : String(leftValue)}
                                      </span>
                                    </>
                                  ) : (
                                    <span>
                                      {typeof foundValue === "object"
                                        ? renderObjectRecursively(foundValue)
                                        : String(foundValue ?? null)}
                                    </span>
                                  )}
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <li>{t("no_comparison_data")}</li>
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              {t("button_close")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

Logs.propTypes = {
  isAdminView: PropTypes.bool,
};

export default Logs;
